import { useState } from 'react'
import { Blocker, useBlocker, useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { Button, Modal } from '@ui/components/core'
import { DangerIcon } from '@ui/components/icons'
import { useSavePipeline } from '@ui/lib/hooks/useSavePipeline'
import { usePipelineModificationStore, usePipelinesStore } from '@ui/stores'

export const PipelineModifiedWrapper = ({ children }: { children: React.ReactNode }) => {
  const { pipelineId } = useParams<{ pipelineId: string }>()

  const [openModal, setOpenModal] = useState(false)

  const { updateWorkflow } = useSavePipeline()

  const { isModified } = usePipelineModificationStore(
    useShallow((state) => ({
      isModified: state.isModified,
    }))
  )

  const { selectedPipeline } = usePipelinesStore(
    useShallow((state) => ({
      selectedPipeline: state.selectedPipeline,
    }))
  )

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const [currentPipelinePath] = currentLocation.pathname.match(/pipelines\/([a-fA-F0-9-]+)/) || []
    const isNavigatingAwayFromCurrentPipeline = Boolean(
      currentPipelinePath && !nextLocation.pathname.includes(currentPipelinePath)
    )

    return isModified && isNavigatingAwayFromCurrentPipeline
  })

  const handleSaveAndLeave = async (blocker: Blocker) => {
    try {
      if (!selectedPipeline) {
        throw new Error('No pipeline selected')
      }

      const isNew = !pipelineId
      const pipelineName = selectedPipeline?.name
      const currentPipelineEnabled = selectedPipeline?.enabled

      const { data } = await updateWorkflow({
        pipeline: selectedPipeline!,
        isNew,
        name: pipelineName,
        enabled: currentPipelineEnabled,
      })

      if (blocker.proceed && data) {
        blocker.proceed()
      }
    } catch (error) {
      console.error('Error saving pipeline:', error)
    }
  }

  const handleLeaveWithoutSaving = () => {
    if (blocker.proceed) {
      blocker.proceed()
      setOpenModal(false)
    }
  }

  const handleCancel = () => {
    if (blocker.reset) {
      blocker.reset()
      setOpenModal(false)
    }
  }

  return (
    <>
      <Modal isOpen={blocker.state === 'blocked' && isModified && !openModal} onClose={handleCancel}>
        <div className="flex flex-col space-y-4">
          <div className="flex flex-row items-center space-x-2 p-6">
            <DangerIcon className="h-7 w-7 stroke-red-500" />
            <p className="text-lg font-medium">Unsaved Changes</p>
          </div>
          <div className="flex flex-col space-y-4">
            <p className="px-6 text-sm text-gray-500">
              Are you sure you want to leave this page? You have unsaved changes that will be lost.
            </p>

            <div className="h-px w-full bg-gray-200" />

            <div className="flex flex-row justify-between px-6 pb-6">
              <Button variant="danger" onClick={handleLeaveWithoutSaving}>
                Leave without saving
              </Button>

              <div className="flex flex-row space-x-2">
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    handleSaveAndLeave(blocker)
                  }}>
                  Save and leave
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      {/* <ActionModal
        open={blocker.state === 'blocked' && isModified && !openModal}
        title="Unsaved Changes on Pipeline"
        message="Are you sure you want to leave this page? You have unsaved changes that will be lost."
        handleAction={() => {
          if (blocker.reset) {
            blocker.reset()
            setOpenModal(false)
          }
        }}
        actionIdentifier="Stay"
        dismissAction={() => {
          if (blocker.proceed) {
            blocker.proceed()
          }
        }}
        dismissIdentifier="Continue"
      /> */}
      {children}
    </>
  )
}
